/**
 * Toolbar for log panel: search, display options, download, fullscreen.
 */

import { Input, Checkbox, Button, Space } from 'antd';
import {
  UpOutlined,
  DownOutlined,
  DownloadOutlined,
  FullscreenOutlined,
  FullscreenExitOutlined,
} from '@ant-design/icons';

interface LogToolbarProps {
  // Search
  searchKeyword: string;
  onSearchChange: (keyword: string) => void;
  matchCount: number;
  currentMatchIndex: number;
  onSearchPrev: () => void;
  onSearchNext: () => void;

  // Display options
  wrapText: boolean;
  onWrapTextChange: (checked: boolean) => void;
  hideTimestamp: boolean;
  onHideTimestampChange: (checked: boolean) => void;
  darkTheme: boolean;
  onDarkThemeChange: (checked: boolean) => void;

  // Actions
  onDownload: () => void;
  downloadDisabled: boolean;
  isFullscreen: boolean;
  onToggleFullscreen: () => void;
}

const LogToolbar: React.FC<LogToolbarProps> = ({
  searchKeyword,
  onSearchChange,
  matchCount,
  currentMatchIndex,
  onSearchPrev,
  onSearchNext,
  wrapText,
  onWrapTextChange,
  hideTimestamp,
  onHideTimestampChange,
  darkTheme,
  onDarkThemeChange,
  onDownload,
  downloadDisabled,
  isFullscreen,
  onToggleFullscreen,
}) => {
  const hasMatches = matchCount > 0;
  const matchDisplay = searchKeyword
    ? `${hasMatches ? currentMatchIndex + 1 : 0}/${matchCount}`
    : '';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        padding: '4px 12px',
        gap: 12,
        borderBottom: '1px solid #e8e8e8',
        backgroundColor: darkTheme ? '#252526' : '#f5f5f5',
        flexWrap: 'wrap',
        minHeight: 36,
      }}
    >
      {/* Left: search */}
      <Space size={4}>
        <Input
          placeholder="Search..."
          size="small"
          style={{ width: 180 }}
          value={searchKeyword}
          onChange={(e) => onSearchChange(e.target.value)}
          allowClear
        />
        {matchDisplay && (
          <span style={{ fontSize: 12, color: darkTheme ? '#aaa' : '#666', whiteSpace: 'nowrap' }}>
            {matchDisplay}
          </span>
        )}
        <Button
          size="small"
          type="text"
          icon={<UpOutlined />}
          disabled={!hasMatches}
          onClick={onSearchPrev}
        />
        <Button
          size="small"
          type="text"
          icon={<DownOutlined />}
          disabled={!hasMatches}
          onClick={onSearchNext}
        />
      </Space>

      {/* Middle: display options */}
      <Space size={12}>
        <Checkbox checked={wrapText} onChange={(e) => onWrapTextChange(e.target.checked)}>
          <span style={{ fontSize: 12 }}>Wrap text</span>
        </Checkbox>
        <Checkbox checked={hideTimestamp} onChange={(e) => onHideTimestampChange(e.target.checked)}>
          <span style={{ fontSize: 12 }}>Hide timestamp</span>
        </Checkbox>
        <Checkbox checked={darkTheme} onChange={(e) => onDarkThemeChange(e.target.checked)}>
          <span style={{ fontSize: 12 }}>Dark theme</span>
        </Checkbox>
      </Space>

      {/* Right: actions */}
      <Space size={4} style={{ marginLeft: 'auto' }}>
        <Button
          size="small"
          type="text"
          icon={<DownloadOutlined />}
          disabled={downloadDisabled}
          onClick={onDownload}
        />
        <Button
          size="small"
          type="text"
          icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
          onClick={onToggleFullscreen}
        />
      </Space>
    </div>
  );
};

export default LogToolbar;
